import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import logo from './images/spore-logo.png';
import { mushroomTypes } from './constants';

// Same D1-backed API the main tracker uses.
const API_URL = '/api/batches';

const DATE_RANGES = {
  month: { label: 'Past Month', days: 30 },
  '90days': { label: 'Past 90 Days', days: 90 },
  year: { label: 'Past Year', days: 365 }
};

// Only these two block sizes get averaged in the report (Grow Kits are
// excluded since they aren't sold/tracked in lb blocks).
const BLOCK_SIZES = ['5lb Bag', '10lb Bag'];

const Reports = () => {
  const [batches, setBatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');

  useEffect(() => {
    const loadBatches = async () => {
      try {
        const res = await fetch(API_URL);
        if (!res.ok) throw new Error(`Failed to load batches (${res.status})`);
        const data = await res.json();
        setBatches(data);
      } catch (error) {
        console.error('Error loading batches:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadBatches();
  }, []);

  const cutoffDate = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - DATE_RANGES[dateRange].days);
    return cutoff.toISOString().split('T')[0];
  }, [dateRange]);

  // For every mushroom type + block size, total up lbs harvested and bags
  // harvested within the selected window, then derive a weighted average
  // (total lbs / total blocks) rather than averaging each batch's average,
  // so larger batches count proportionally more toward the result.
  const rows = useMemo(() => {
    const totals = {};
    Object.keys(mushroomTypes).forEach(type => {
      totals[type] = {
        '5lb Bag': { weight: 0, bags: 0 },
        '10lb Bag': { weight: 0, bags: 0 }
      };
    });

    batches.forEach(batch => {
      if (!BLOCK_SIZES.includes(batch.bagType)) return;
      if (!totals[batch.mushroomType]) return; // unrecognized/legacy type

      (batch.harvestEntries || []).forEach(entry => {
        if (entry.date < cutoffDate) return;
        totals[batch.mushroomType][batch.bagType].weight += entry.weight;
        totals[batch.mushroomType][batch.bagType].bags += entry.bagsHarvested;
      });
    });

    return Object.keys(mushroomTypes).map(type => {
      const fiveLb = totals[type]['5lb Bag'];
      const tenLb = totals[type]['10lb Bag'];
      return {
        type,
        fiveLbAvg: fiveLb.bags > 0 ? fiveLb.weight / fiveLb.bags : null,
        fiveLbBags: fiveLb.bags,
        tenLbAvg: tenLb.bags > 0 ? tenLb.weight / tenLb.bags : null,
        tenLbBags: tenLb.bags
      };
    });
  }, [batches, cutoffDate]);

  const hasAnyData = rows.some(r => r.fiveLbBags > 0 || r.tenLbBags > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50 p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
          <div className="flex items-center gap-4">
            <img className="max-w-32 max-h-32" src={logo} alt="Logo" />
            <h1 className="text-2xl md:text-3xl font-bold font-[Syncopate] text-blue-700">Yield Reports</h1>
          </div>
          <Link
            to="/"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 shadow-lg transition"
          >
            <ArrowLeft size={20} />
            Back to Batches
          </Link>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Date Range</label>
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="w-full md:w-64 border-2 border-gray-300 rounded-lg px-4 py-2 focus:border-emerald-500 focus:outline-none"
          >
            {Object.entries(DATE_RANGES).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-600">Loading report...</div>
          ) : (
            <table className="w-full text-left">
              <thead className="bg-emerald-50">
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Mushroom Type</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Avg Yield / 5lb Block</th>
                  <th className="px-4 py-3 text-sm font-semibold text-gray-700">Avg Yield / 10lb Block</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.type} className="border-t border-gray-100">
                    <td className="px-4 py-3 font-medium text-gray-800">{row.type}</td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.fiveLbAvg !== null
                        ? `${row.fiveLbAvg.toFixed(2)} lbs (${row.fiveLbBags} block${row.fiveLbBags === 1 ? '' : 's'})`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {row.tenLbAvg !== null
                        ? `${row.tenLbAvg.toFixed(2)} lbs (${row.tenLbBags} block${row.tenLbBags === 1 ? '' : 's'})`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!isLoading && !hasAnyData && (
            <p className="px-4 py-4 text-sm text-gray-500 border-t border-gray-100">
              No harvests recorded in this date range yet.
            </p>
          )}
        </div>

        <p className="text-xs text-gray-500 mt-4">
          Averages are weighted by harvested block count (total lbs harvested ÷ total blocks harvested)
          within the selected date range, based on each harvest entry's recorded date.
        </p>
      </div>
    </div>
  );
};

export default Reports;
